import Results from "@/components/Autocomplete/Results/Results"
import { useDomEvents } from "@/hooks/useDomEvents"
import { usePopState } from "@/hooks/usePopState"
import { selectors } from "@/config"
import { useAutocomplete } from "./useAutocomplete"
import { SearchAnalyticsOptions } from "@nosto/nosto-js/client"
import { OnSubmitProvider } from "./OnSubmitContext"
import { useRef, useEffect } from "preact/hooks"
import { useNostoAppState } from "@nosto/search-js/preact/hooks"
import { QUERY_PARAM } from "@/mapping/url/constants"

type Props = {
  onSubmit: (input: string, options?: SearchAnalyticsOptions) => void
}

export default function AutocompleteInjected({ onSubmit }: Props) {
  const dropdownRef = useRef<HTMLElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const searchFormRef = useRef<HTMLFormElement | null>(null)

  useEffect(() => {
    dropdownRef.current = document.querySelector<HTMLElement>(selectors.dropdown)
    searchInputRef.current = document.querySelector<HTMLInputElement>(selectors.searchInput)
    searchFormRef.current = document.querySelector<HTMLFormElement>(selectors.searchForm)
  }, [])

  const redirect = useNostoAppState(state => state.response?.redirect)

  const { input, showAutocomplete, setInput, setShowAutocomplete, onSearchSubmit, onKeyDown } = useAutocomplete({
    onSubmit,
    searchInputRef,
    clickOutsideTargetRef: dropdownRef,
    isInjected: true
  })

  usePopState(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const query = searchParams.get(QUERY_PARAM) || ""
    if (searchInputRef.current && query) {
      searchInputRef.current.value = query
    }
  })

  useDomEvents(searchInputRef, {
    onInput: () => setInput(searchInputRef.current?.value || ""),
    onFocus: () => setShowAutocomplete(true),
    onKeyDown
  })

  useDomEvents(searchFormRef, {
    onSubmit: e => {
      e.preventDefault()
      if (redirect) {
        // Direct navigation is intentional for redirect rules (can be external URLs)
        window.location.href = redirect
      } else {
        onSearchSubmit(input)
      }
    }
  })

  return showAutocomplete ? (
    <OnSubmitProvider onSubmit={onSearchSubmit}>
      <Results input={input} onKeyDown={onKeyDown} />
    </OnSubmitProvider>
  ) : null
}
